import React, { useState, useEffect } from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import {
  QrcodeOutlined,
  PushpinOutlined,
  IdcardOutlined,
  KeyOutlined,
  LinkOutlined,
  ExportOutlined,
  CopyOutlined,
  SendOutlined,
  TabletOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Card,
  Col,
  Image,
  Input,
  InputNumber,
  Row,
  Switch,
  Tag as AntTag,
  Typography,
  message,
  Modal,
} from 'antd';
import firebase from 'firebase/app';
import { Link } from 'react-router-dom';

import { posPaymentUrl, posPaymentQRUrl, getPayerBaseUrl } from '../../utils/URLBuilder';
import { deviceKindLabel } from '../../utils/deviceCategory';
import TrackerService from '../../utils/TrackerService';
import { getPaymentAmounts } from '../../utils/feeCalculation';

import DeviceImage from './DeviceImage';


const { Text, Title } = Typography;

const currencySymbols = { EUR: '€', USD: '$', GBP: '£' };

/** Operator-facing name, identical to the reader's: first name + last
 * initial ('Charles D.') — the operator sees what the customer will see. */
function firstNameWithInitial(name) {
  const tokens = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return '';
  if (tokens.length === 1) return tokens[0];
  return `${tokens[0]} ${tokens[tokens.length - 1][0].toUpperCase()}.`;
}


// Physical-fixture identity for a device nobody has nicknamed yet. A raw
// tmr_/tag id says nothing at the counter; the serial tail is printed on the
// terminal's back ("SN ***4444"), and a QR fixture's id head+tail matches
// what's on the printed code — enough to find the unit before stickering it.
const deviceShortId = (device) => {
  if (device.deviceKind === 'terminal') {
    // Uppercased to match the label printed on the device's back.
    return device.deviceSerial
      ? `SN ${String(device.deviceSerial).slice(-4).toUpperCase()}`
      : String(device.tagUid || device.id || '');
  }
  // <=15 chars: the whole id fits and elision would only hide it.
  const id = String(device.tagUid || device.id || '');
  return id.length > 15 ? `QR ${id.slice(0, 4)}—${id.slice(-4)}` : id;
};

/** Isolated so the 1s tick re-renders only this line, never the whole screen. */
const SessionCountdown = ({ expiresAt, onMoreTime, onExpired }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
  useEffect(() => {
    if (remaining === 0 && onExpired) onExpired();
  }, [remaining === 0]);
  return (
    <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
      {`Expires in ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`}
      <Button size="small" onClick={onMoreTime} style={{ marginLeft: '8px' }}>
        +5 min
      </Button>
    </Text>
  );
};

/**
 * Smart POS: the operator flow top to bottom — Service (what was provided, the
 * bill total, who the customer is) → who provided it (faces) → tip selection →
 * totals (canonical fee calculation) → HAND OFF to a device → share options.
 *
 * The service total (t=) is required: percentage tips are computed off it and
 * it keeps reporting consistent. Strikepay processes the TIP payment — the bill
 * itself is settled at the till; the total is context, not a charge.
 *
 * "Open tipping page" deliberately opens WITHOUT the staff session — the page
 * is being handed to a customer, who should start as an anonymous payer.
 */
const TeamSmartPosBody = ({
  locStrings,
  teamId,
  team,
  clientId,
  teamMembers,
  isAdmin,
}) => {
  const tagId = team.defaultTagId;
  const currency = team.defaultCurrency || 'EUR';
  const symbol = currencySymbols[currency] || `${currency} `;
  const activeMembers = (teamMembers || []).filter((m) => !m.isDeleted);

  // Percents are basis points (1200 = 12%) off the service total; values are
  // minor units. Smart POS reads TEAM presets only — team settings are the
  // source of truth. Tag-level percents/values are a legacy hang-over and
  // deliberately NOT inherited here (the payer page still merges them); a
  // team without team-level presets gets a settings hint, not tag fallback.
  const percentPresets = (team.percents || []).map(Number).filter((n) => n > 0);
  const valuePresets = percentPresets.length === 0
    ? (team.values || []).map(Number).filter((n) => n > 0)
    : [];

  // Box 1 — Service
  const [reference, setReference] = useState('');
  const [totalMajor, setTotalMajor] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  // Honors the team's tap behaviour: 'team' → whole-team only; 'members' →
  // member faces + customer-picks only; 'both' → everything.
  const behaviour = team.teamTapBehaviour || 'both';
  const allowWholeTeam = behaviour !== 'members';
  const allowMembers = behaviour !== 'team';
  // Box 2 — Provided by: '' = whole team, '__pick__' = customer picks, else tmid
  const [memberMode, setMemberMode] = useState(allowWholeTeam ? '' : '__pick__');
  // Big rosters (the launch team is 50 strong): type-ahead filter over names.
  const [memberQuery, setMemberQuery] = useState('');
  // Pooled venues register a stand per TABLE — the device wall needs one too.
  const [deviceQuery, setDeviceQuery] = useState('');
  // Box 3 — Tip
  const [amountMode, setAmountMode] = useState('customer'); // customer | preset | custom
  const [presetIndex, setPresetIndex] = useState(null);
  const [amountMajor, setAmountMajor] = useState(null);
  // Hand-off + share
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  // Share links must NEVER target a device tag — device URLs are armed-scan
  // surfaces. If the team's defaultTagId is itself a stand/terminal, fall
  // back to the first live non-device tag; null = nothing safe to share.
  const [shareTagId, setShareTagId] = useState(team.defaultTagId || null);

  const [arming, setArming] = useState(false);
  // Presentational-only local touches (never authoritative): a countdown that
  // ran out before the server's lazy expiry write, and the fetched receipt.
  const [expiredRefIds, setExpiredRefIds] = useState([]);
  const [paidTaps, setPaidTaps] = useState({}); // refId → tap
  // Optimistic badge: HAND OFF → 'Activating…' until the device doc's
  // activeTapId streams back in ('In Use'), or the arm fails ('Ready').
  const [activatingDeviceId, setActivatingDeviceId] = useState(null);
  const [isQRVisible, setIsQRVisible] = useState(false);
  const [feesMap, setFeesMap] = useState(null);
  const [sites, setSites] = useState([]);
  const [posHistory, setPosHistory] = useState([]);
  const myPermissions = ((teamMembers || []).find((m) => m.clientId === clientId) || {}).permissions || {};
  const canSeeAllPosTaps = isAdmin || !!myPermissions.isTeamAdmin || !!myPermissions.canSeeAllTxns
    || team.teamOwnerClientId === clientId || team.teamClientId === clientId;
  // Starter-or-manager: who may END a live session from the device card.
  // Server enforces the same rule; this only decides what the card offers.
  const canManageSessions = isAdmin || !!myPermissions.isTeamAdmin
    || team.teamOwnerClientId === clientId || team.teamClientId === clientId;
  const [shouldAddOnFees, setShouldAddOnFees] = useState(true);

  const totalMinor = totalMajor > 0 ? Math.round(totalMajor * 100) : null;

  let amountMinor = null;
  if (amountMode === 'custom' && amountMajor > 0) {
    amountMinor = Math.round(amountMajor * 100);
  } else if (amountMode === 'preset' && presetIndex != null) {
    if (percentPresets.length > 0) {
      amountMinor = totalMinor
        ? Math.round((totalMinor * percentPresets[presetIndex]) / 10000)
        : null;
    } else {
      amountMinor = valuePresets[presetIndex] || null;
    }
  }

  const needsTotal = !totalMinor; // service amount is required
  const tipUndecided = (amountMode === 'custom' && !amountMinor)
    || (amountMode === 'preset' && amountMinor == null);
  const notReady = needsTotal || tipUndecided;

  // Fee slabs are banded by amount (fees_<product>_slab_above_<major units>):
  // pick the highest band at or under the tip, canonical formula below.
  const slabForAmount = (minor) => {
    if (!feesMap || !minor) return null;
    let best = null;
    let bestThreshold = -1;
    Object.keys(feesMap).forEach((key) => {
      const m = key.match(/_above_(\d+)$/);
      const threshold = m ? Number(m[1]) * 100 : 0;
      if (threshold <= minor && threshold > bestThreshold) {
        bestThreshold = threshold;
        best = feesMap[key];
      }
    });
    return best;
  };
  const feeView = amountMinor
    ? getPaymentAmounts(amountMinor, shouldAddOnFees, slabForAmount(amountMinor))
    : null;

  useEffect(() => {
    // Fee slabs + fee options come from the payer's expanded tag JSON.
    fetch(`${getPayerBaseUrl()}/api/tags/${tagId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!json) return;
        if (json.fees) setFeesMap(json.fees);
        if (json.feeOptions && json.feeOptions.includeFeesByDefault != null) {
          setShouldAddOnFees(!!json.feeOptions.includeFeesByDefault);
        }
      })
      .catch(() => {});
  }, [tagId]);

  useEffect(() => {
    // The team's registered devices: tags with deviceKind set. Filtered
    // client-side to avoid a composite index; a team owns a handful of tags.
    const db = firebase.firestore();
    const unsubscribe = db.collection('tags')
      .where('teamId', '==', teamId)
      .onSnapshot(
        (snap) => {
          const all = [];
          snap.forEach((doc) => all.push({ id: doc.id, ...doc.data() }));
          setDevices(all.filter((d) => d.deviceKind && d.status === 'live'));
          const defaultTag = all.find((t) => t.id === team.defaultTagId);
          if (defaultTag && defaultTag.deviceKind) {
            const alt = all.find((t) => !t.deviceKind && t.status === 'live');
            setShareTagId(alt ? alt.id : null);
          } else {
            setShareTagId(team.defaultTagId || null);
          }
        },
        (err) => console.warn('[SmartPOS] device listing failed', err && err.message),
      );
    return unsubscribe;
  }, [teamId]);

  useEffect(() => {
    // Sites shown to the customer after payment — displayed on the PAID view.
    const db = firebase.firestore();
    const unsubscribe = db.collection('teams').doc(teamId).collection('sites')
      .onSnapshot(
        (snap) => {
          const found = [];
          snap.forEach((doc) => found.push({ id: doc.id, ...doc.data() }));
          setSites(found);
        },
        () => {},
      );
    return unsubscribe;
  }, [teamId]);

  const paidCount = devices.filter((d) => d.activeHandoff
    && d.activeHandoff.status === 'successful').length;
  useEffect(() => {
    // The POS's own ledger. Fetch on PAID transitions — twice, because the tap
    // doc's paid status can land a beat after the device stream flips — and on
    // a slow interval so colleagues' payments appear without a refresh.
    let cancelled = false;
    const fetchHistory = async () => {
      try {
        const idToken = await firebase.auth().currentUser.getIdToken();
        const res = await fetch(`${getPayerBaseUrl()}/api/teams/${teamId}/pos/history`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (res.ok) {
          const json = await res.json();
          if (!cancelled) setPosHistory(json.taps || []);
        }
      } catch (e) { /* next refresh will catch up */ }
    };
    fetchHistory();
    const settle = setTimeout(fetchHistory, 4000);
    const slow = setInterval(fetchHistory, 60000);
    return () => { cancelled = true; clearTimeout(settle); clearInterval(slow); };
  }, [teamId, paidCount]);

  // ---- Sessions are PER-DEVICE views of the stream -------------------------
  // Many operators run the POS at once; each DEVICE is one-at-a-time. A busy
  // device is unselectable for everyone; each operator sees panels only for
  // the sessions THEY initiated (activeHandoff.createdByClientId).
  const TERMINAL_DISPLAY_MS = 3 * 60 * 1000;
  const sessionTipLabel = (session) => {
    if (session.amount != null) return `${symbol}${(session.amount / 100).toFixed(2)}`;
    if (session.selectedIndex != null) {
      // Payer convention: with N presets, index 0..N-1 selects one and
      // index >= N opens the CUSTOM amount slot.
      const presets = percentPresets.length > 0 ? percentPresets : valuePresets;
      if (session.selectedIndex >= presets.length) return 'Custom amount';
      if (percentPresets.length > 0) {
        const bp = percentPresets[session.selectedIndex];
        const computed = session.inputTotal != null
          ? ` — ${symbol}${((session.inputTotal * bp) / 10000 / 100).toFixed(2)}` : '';
        return `${bp / 100}%${computed}`;
      }
      return `${symbol}${(valuePresets[session.selectedIndex] / 100).toFixed(2)}`;
    }
    return 'Customer chooses';
  };
  const mySessions = devices
    .filter((d) => d.activeHandoff && d.activeHandoff.refId
      && d.activeHandoff.createdByClientId === clientId)
    .map((d) => {
      const handoff = d.activeHandoff;
      let sessionStatus = handoff.status || 'created';
      if (sessionStatus === 'created' && expiredRefIds.includes(handoff.refId)) {
        sessionStatus = 'expired';
      }
      const endedAtMs = handoff.endedAt && handoff.endedAt.toMillis
        ? handoff.endedAt.toMillis() : null;
      const displayOver = ['successful', 'cancelled', 'expired', 'failed', 'declined'].includes(sessionStatus)
        && endedAtMs && Date.now() - endedAtMs > TERMINAL_DISPLAY_MS;
      if (displayOver) return null;
      const md = d.metadata || {};
      return {
        deviceTagId: d.id,
        deviceName: md.nickname || md.name || md.placeName || md.place || deviceShortId(d) || d.claimTerm || d.deviceKind,
        deviceKind: d.deviceKind,
        tapId: handoff.tapId,
        refId: handoff.refId,
        createdByClientId: handoff.createdByClientId || null,
        // Display context stamped at arm time — what was handed off.
        amount: handoff.amount != null ? handoff.amount : null,
        inputTotal: handoff.inputTotal != null ? handoff.inputTotal : null,
        selectedIndex: handoff.selectedIndex != null ? handoff.selectedIndex : null,
        customerPicks: !!handoff.customerPicks,
        awaitingConfirm: !!handoff.awaitingConfirm,
        teamMemberName: handoff.teamMemberName || null,
        customerName: handoff.customerName || null,
        customerEmail: handoff.customerEmail || null,
        serviceDescription: handoff.serviceDescription || null,
        // Terminal drive stamps: the exact amount on the reader right now.
        chargeTotal: handoff.chargeTotal != null ? handoff.chargeTotal : null,
        addOnFees: handoff.addOnFees != null ? handoff.addOnFees : null,
        expiresAt: d.activeTapExpiresAt ? d.activeTapExpiresAt.toDate().toISOString() : null,
        status: sessionStatus,
        tap: paidTaps[handoff.refId] || null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.deviceName || '').localeCompare(b.deviceName || ''));

  useEffect(() => {
    // One-shot receipt fetch per PAID session — display data, not state.
    const missing = mySessions.find((sess) => sess.status === 'successful'
      && sess.tapId && !paidTaps[sess.refId]);
    if (!missing) return;
    fetch(`${getPayerBaseUrl()}/api/tags/${missing.deviceTagId}/${missing.tapId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (json) {
          const paidTap = json.tap || json;
          setPaidTaps((prev) => ({ ...prev, [missing.refId]: paidTap }));
          // Session funnel closer — fires exactly once per paid session (this
          // fetch is one-shot per refId). Purchase tracks the PAYMENT under
          // the payer's identity; this tracks the SESSION under the
          // operator's, so armed→completed funnels work in Mixpanel.
          new TrackerService({ isTest: !!team.is_test }).mixpanelTrack('smartpos_session_completed', {
            teamId,
            teamName: team.teamName,
            currency,
            refId: missing.refId,
            deviceTagId: missing.deviceTagId,
            deviceKind: missing.deviceKind,
            amount: paidTap.amount ?? null,
            fee: paidTap.fee ?? null,
            memberClientId: paidTap.teamMemberId || null,
            displayName: paidTap.teamMemberName || null,
            hasServiceDescription: !!paidTap.serviceDescription,
            clientId,
            createdByClientId: missing.createdByClientId || clientId,
            teamOwnerClientId: team.teamOwnerClientId || team.teamClientId || null,
          });
        }
      })
      .catch(() => {});
  });

  const tmid = memberMode && memberMode !== '__pick__' ? memberMode : null;
  const linkOpts = {
    amountMinor,
    totalMinor,
    tmid,
    customerPicks: memberMode === '__pick__',
    name: customerName.trim() || null,
    serviceDescription: reference.trim() || null,
  };
  const paymentLink = shareTagId ? posPaymentUrl(shareTagId, linkOpts) : null;
  const qrImageSrc = shareTagId ? posPaymentQRUrl(shareTagId, linkOpts) : null;

  // Stands require NOTHING: a bare hand-off just reserves the stand and the
  // customer chooses everything on their device. Since the four-stage flow
  // (strike-payer #352) terminals have their own selection screen, so
  // "Customer chooses" is valid there too — PROVIDED the reader will have
  // buttons to offer: value presets always resolve; percent presets need the
  // service amount to compute against; a chosen tip resolves by itself.
  // Only the staff-member chooser stays stand-only: terminals have no member
  // selection screen.
  const selectedDevice = devices.find((d) => d.id === selectedDeviceId) || null;
  const isTerminalTarget = selectedDevice && selectedDevice.deviceKind === 'terminal';
  const pickOnStand = memberMode === '__pick__' && selectedDevice
    && selectedDevice.deviceKind !== 'terminal';
  const terminalTipResolvable = !!amountMinor
    || valuePresets.length > 0
    || (percentPresets.length > 0 && !!totalMinor);
  const canHandOff = !!selectedDevice
    && (!isTerminalTarget || terminalTipResolvable)
    && (memberMode !== '__pick__' || pickOnStand);

  const [idleModeBusyId, setIdleModeBusyId] = useState(null);
  // "Always on" (kiosk mode): the terminal shows a standing tip screen while
  // idle. Server-side flip + immediate arm; the device card re-renders from
  // the tags snapshot when terminalIdleMode changes.
  const setIdleMode = async (device, on) => {
    setIdleModeBusyId(device.id);
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const res = await fetch(
        `${getPayerBaseUrl()}/api/teams/${teamId}/pos/devices/${device.id}/idle-mode`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ clientId, mode: on ? 'tip-kiosk' : 'handoff' }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Say WHY: auth (401/403), wrong team (404), server mid-restart (5xx).
        const reason = json.error || `HTTP ${res.status}`;
        message.error(`Could not change Always on — ${reason}${res.status >= 500 ? ' (server busy? try again)' : ''}`);
        return;
      }
      if (!on) message.success('Always on turned off — screen clears shortly');
      else if (json.armOutcome === 'armed') message.success('Always on — tip screen showing');
      else message.warning(`Always on saved — screen not up yet (${json.armOutcome})`);
    } catch (err) {
      message.error('Could not change Always on — network error, try again');
    } finally {
      setIdleModeBusyId(null);
    }
  };

  const handOff = async () => {
    if (!canHandOff) return;
    setArming(true);
    setActivatingDeviceId(selectedDeviceId);
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const res = await fetch(`${getPayerBaseUrl()}/api/teams/${teamId}/pos/handoffs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          clientId, // the client this POS acts as — server-verified
          deviceTagId: selectedDeviceId,
          // Presets travel as the index — the payer renders them as if the
          // customer tapped the preset; only Custom pins a literal amount.
          amount: amountMode === 'custom' ? amountMinor : null,
          selectedIndex: amountMode === 'preset' && amountMinor != null ? presetIndex : null,
          total: totalMinor,
          tmid,
          customerPicks: pickOnStand || null,
          customerName: customerName.trim() || null,
          customerEmail: customerEmail.trim() || null,
          serviceDescription: reference.trim() || null,
          // The fee posture is part of the selection, for every device kind:
          // terminals charge it exactly (no payer UI exists); QR hand-offs
          // pre-set the payer page with it.
          addOnFees: shouldAddOnFees,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setActivatingDeviceId(null);
        // Terminal drive failures come back as specific codes — say what
        // actually happened at the reader, never a generic failure.
        const errorText = {
          'device-busy': 'That device is already running a payment — cancel it first.',
          'finishing-up': 'The last customer is still on the receipt screen — try again in a moment.',
          'screen-in-use': 'The customer is still using the terminal — wait for them to finish.',
          'reader-offline': 'The reader is offline — check its power and network, then try again.',
          'reader-busy': 'The reader is busy with another payment.',
          'reader-timeout': 'The reader timed out — try again.',
          'reader-drive-failed': 'Couldn\u2019t reach the reader — try again in a moment.',
          'tip-required': 'Terminals need a tip amount — pick a preset or enter one before handing off.',
          'not-a-driveable-terminal': 'This terminal can\u2019t be driven remotely.',
          'invalid-email': 'That customer email doesn\u2019t look right — fix it or clear it.',
        }[json.error];
        message.error(errorText || `Hand off failed (${json.error || res.status})`);
        return;
      }
      resetDraft();
      message.success('Device armed — waiting for the customer');
      const handedDevice = devices.find((d) => d.id === selectedDeviceId) || {};
      const pickedMember = tmid
        ? (teamMembers || []).find((m) => (m.id || m.clientId) === tmid) : null;
      new TrackerService({ isTest: !!team.is_test }).mixpanelTrack('smartpos_session_handoff', {
        teamId,
        teamName: team.teamName,
        currency,
        deviceTagId: selectedDeviceId,
        deviceKind: handedDevice.deviceKind,
        templateTagId: handedDevice.templateTagId || null,
        refId: json.refId,
        totalMinor,
        tipMode: amountMode, // customer | preset | custom
        customerPicks: !!pickOnStand,
        hasMember: !!tmid,
        hasServiceDescription: !!reference.trim(),
        hasCustomerEmail: !!customerEmail.trim(),
        addOnFees: shouldAddOnFees,
        // Categorization spine: who ran the till, who receives, who owns.
        clientId,
        memberClientId: tmid || null,
        displayName: pickedMember ? pickedMember.displayName : null,
        teamOwnerClientId: team.teamOwnerClientId || team.teamClientId || null,
      });
    } catch (e) {
      setActivatingDeviceId(null);
      message.error('Hand off failed — check your connection');
    } finally {
      setArming(false);
    }
  };

  const moreTime = async (sess) => {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const res = await fetch(
        `${getPayerBaseUrl()}/api/teams/${teamId}/pos/handoffs/${sess.deviceTagId}/extend`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          // Sessions start at 15 min; the button is a +5 top-up from NOW.
          body: JSON.stringify({ clientId, ttlSeconds: 300 }),
        },
      );
      const json = await res.json();
      if (res.ok && json.expiresAt) {
        message.success('5 more minutes');
      }
    } catch (e) {
      message.error('Could not extend — check your connection');
    }
  };

  // Each customer's context is entered fresh: hand-off CONSUMES the draft
  // (the session panel carries it from there), so nothing from customer 1
  // lingers in the form while customer 2 is being rung up.
  const resetDraft = () => {
    setSelectedDeviceId(null);
    setActivatingDeviceId(null);
    setTotalMajor(null);
    setReference('');
    setCustomerName('');
    setCustomerEmail('');
    setAmountMode('customer');
    setPresetIndex(null);
    setAmountMajor(null);
    setMemberMode(allowWholeTeam ? '' : '__pick__');
    setIsQRVisible(false);
  };

  const startNewPayment = async (deviceTagId) => {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      await fetch(
        `${getPayerBaseUrl()}/api/teams/${teamId}/pos/handoffs/${deviceTagId}/dismiss`,
        { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } },
      );
    } catch (e) { /* the stream still rules; the next arm overwrites anyway */ }
    resetDraft();
  };

  const cancelSession = async (sess) => {
    if (!sess) return;
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const res = await fetch(
        `${getPayerBaseUrl()}/api/teams/${teamId}/pos/handoffs/${sess.deviceTagId}/cancel`,
        { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } },
      );
      const json = await res.json();
      if (json.outcome === 'already-paid') {
        message.info('Too late to cancel — the customer already paid.');
      }
      new TrackerService({ isTest: !!team.is_test }).mixpanelTrack('smartpos_session_cancelled', {
        teamId,
        teamName: team.teamName,
        currency,
        deviceTagId: sess.deviceTagId,
        deviceKind: sess.deviceKind,
        refId: sess.refId,
        // 'cancelled' | 'already-paid' | 'not-armed' — what the cancel found.
        outcome: json.outcome || null,
        // Who clicked CANCEL (page's verified acting client) and who armed it —
        // identical today (operators only see their own sessions), but the
        // distinction matters the day admins can cancel anyone's.
        clientId,
        createdByClientId: sess.createdByClientId || clientId,
        teamOwnerClientId: team.teamOwnerClientId || team.teamClientId || null,
      });
    } catch (e) {
      message.error('Cancel failed — check your connection');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink);
      message.success('Payment link copied');
    } catch (e) {
      message.warning('Could not copy — select the link text instead');
    }
  };

  const openTippingPage = () => {
    window.open(paymentLink, '_blank', 'noopener,noreferrer');
  };

  const memberInitials = (name) => (name || 'M').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  // teamTapBehaviour 'team' = pooled only: no member selection exists.
  const pooledOnly = behaviour === 'team';
  const billCards = (
    <>
      {/* Group 2 · Tip — reads top to bottom into totals and hand-off */}
      <Card title="Tip" style={{ marginTop: '16px', borderRadius: '8px' }}>
        {/* The POS is driven BY STAFF — the tip is the customer's choice,
            never the operator's. The instruction keeps that explicit. */}
        <Text style={{ display: 'block', marginBottom: '8px', color: '#27AE60' }}>
          Ask the customer how much they&rsquo;d like to tip &mdash; and tell
          them the total before you hand off.
        </Text>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            type={amountMode === 'customer' ? 'primary' : 'default'}
            onClick={() => { setAmountMode('customer'); setPresetIndex(null); }}
          >
            Customer chooses
          </Button>
          {(percentPresets.length > 0 ? percentPresets : valuePresets).map((preset, index) => {
            const isPercent = percentPresets.length > 0;
            const label = isPercent
              ? `${(preset / 100).toFixed(preset % 100 === 0 ? 0 : 1)}%`
              : `${symbol}${(preset / 100).toFixed(2)}`;
            const computed = isPercent && totalMinor
              ? ` · ${symbol}${((totalMinor * preset) / 1000000).toFixed(2)}`
              : '';
            const isSelected = amountMode === 'preset' && presetIndex === index;
            return (
              <Button
                key={preset}
                type={isSelected ? 'primary' : 'default'}
                onClick={() => { setAmountMode('preset'); setPresetIndex(index); }}
              >
                {label + computed}
              </Button>
            );
          })}
          <Button
            type={amountMode === 'custom' ? 'primary' : 'default'}
            onClick={() => { setAmountMode('custom'); setPresetIndex(null); }}
          >
            Custom
          </Button>
        </div>
        {/* Reserved block: input or hint lives here, so the card never jumps */}
        <div style={{ marginTop: '12px', minHeight: '48px' }}>
          {amountMode === 'custom' && (
            <InputNumber
              prefix={symbol}
              min={0}
              precision={2}
              parser={(value) => {
                const match = String(value || '').replace(/[^\d.]/g, '').match(/^\d*(?:\.\d{0,2})?/);
                return match ? match[0] : '';
              }}
              value={amountMajor}
              onChange={setAmountMajor}
              placeholder="0.00"
              style={{ width: '200px' }}
            />
          )}
          {amountMode === 'preset' && percentPresets.length > 0 && needsTotal && (
            <Text type="warning" style={{ display: 'block' }}>
              Enter the service amount above — percentage tips are based on it.
            </Text>
          )}
          {amountMode === 'customer' && (
            <Text type="secondary" style={{ display: 'block' }}>
              The customer picks the tip on their device
              {percentPresets.length > 0 ? ' — percentages use the service amount above.' : '.'}
            </Text>
          )}
          {percentPresets.length === 0 && valuePresets.length === 0 && (
            <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
              No team tip presets yet — a manager can add percentages or amounts
              in the team settings, and they will appear here and on the customer
              tipping page.
            </Text>
          )}
        </div>
      </Card>

      {/* Group 2 · Totals */}
      <Card title="Totals" style={{ marginTop: '16px', borderRadius: '8px' }}>
        <Row justify="space-between"><Text type="secondary">Service (settled at the till)</Text><Text style={{ fontVariantNumeric: 'tabular-nums' }}>{totalMinor ? `${symbol}${(totalMinor / 100).toFixed(2)}` : '—'}</Text></Row>
        <Row justify="space-between" style={{ marginTop: '4px' }}><Text type="secondary">Tip</Text><Text style={{ fontVariantNumeric: 'tabular-nums' }}>{amountMinor ? `${symbol}${(amountMinor / 100).toFixed(2)}` : '—'}</Text></Row>
        <Row justify="space-between" style={{ marginTop: '4px' }}>
          <Text type="secondary">
            {shouldAddOnFees ? 'Tipping fee (paid by customer)' : 'Tipping fee (absorbed)'}
            <Switch
              size="small"
              data-testid="fee-toggle"
              checked={shouldAddOnFees}
              onChange={setShouldAddOnFees}
              style={{ marginLeft: '8px' }}
              aria-label="Customer covers the tipping fee"
            />
          </Text>
          <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{feeView ? `${symbol}${(feeView.feeTotal / 100).toFixed(2)}` : '—'}</Text>
        </Row>
        <Row>
          <Text style={{ fontSize: '12px', color: '#27AE60' }}>
            Ask the customer if they&rsquo;re happy to cover the tipping fee.
          </Text>
        </Row>
        <Row justify="space-between" style={{ marginTop: '8px', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
          <Text strong>Customer pays now</Text>
          <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>{feeView ? `${symbol}${(feeView.paymentTotal / 100).toFixed(2)}` : '—'}</Text>
        </Row>
        <Row>
          <Text style={{ fontSize: '12px', color: '#27AE60' }}>
            Let the customer know the total to expect.
          </Text>
        </Row>
        {/* Fixed caption line — present in every mode so the card height holds */}
        <Text type="secondary" style={{ display: 'block', marginTop: '6px', fontSize: '12px', minHeight: '18px' }}>
          {(() => {
            if (amountMode === 'customer') return 'Tip, fee and total are chosen on the customer’s device.';
            if (isTerminalTarget) return 'Charged on the terminal exactly as shown.';
            return '\u00A0';
          })()}
        </Text>
      </Card>

    </>
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 12px 32px' }}>
      <Title level={3} style={{ margin: '12px 0 0' }}>Smart POS</Title>
      <Text type="secondary">
        {`Start a payment for ${team.teamName || 'your team'}, then choose how the customer completes it.`}
      </Text>

      <Row gutter={[16, 16]} align="stretch" style={{ marginTop: '4px' }}>
      <Col xs={24} xl={12}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '0 16px 16px', background: '#fafafa' }}>
      {/* Core block 1 · Service: amount | details+customer */}
      <Card title="Service" style={{ marginTop: '16px', borderRadius: '8px' }}>
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12}>
            <Text strong style={{ display: 'block', marginBottom: '6px' }}>Service amount</Text>
            <InputNumber
              prefix={symbol}
              min={0}
              precision={2}
              parser={(value) => {
                const match = String(value || '').replace(/[^\d.]/g, '').match(/^\d*(?:\.\d{0,2})?/);
                return match ? match[0] : '';
              }}
              value={totalMajor}
              onChange={setTotalMajor}
              placeholder="0.00"
              size="large"
              style={{ width: '100%' }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Percentage tips are based on it.
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong style={{ display: 'block', marginBottom: '6px' }}>Service details</Text>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. Full Colour & Blow Dry"
              maxLength={80}
            />
          </Col>
          <Col xs={24} sm={12}>
            <Text strong style={{ display: 'block', marginBottom: '6px' }}>Customer</Text>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Customer name (optional)"
              maxLength={60}
            />
          </Col>
          <Col xs={24} sm={12}>
            <Text strong style={{ display: 'block', marginBottom: '6px' }}>Email</Text>
            <Input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="Customer email (optional)"
              maxLength={120}
            />
            <Text style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#27AE60' }}>
              Let the customer know a confirmation email will be sent &mdash;
              are they OK to get it?
            </Text>
          </Col>
        </Row>
      </Card>

      {/* POOLED-ONLY teams have no member question: drop the one-button
          card and bring the BILL (Tip + Totals) into this column, so the
          right column is the ROOM — the device wall gets full height. */}
      {!pooledOnly && (
        <>
      {/* Group 1 · Service provided by */}
      <Card title="Service provided by" style={{ marginTop: '16px', borderRadius: '8px', flex: 1 }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {allowWholeTeam && (
            <Button
              type={memberMode === '' ? 'primary' : 'default'}
              onClick={() => setMemberMode('')}
              style={{ height: 'auto', padding: '10px 14px', borderRadius: '8px', textAlign: 'center' }}
            >
              <TeamOutlined style={{ fontSize: '28px', display: 'block', margin: '0 auto 4px' }} />
              Whole team
            </Button>
          )}
          {allowMembers && (
            <Button
              type={memberMode === '__pick__' ? 'primary' : 'default'}
              onClick={() => setMemberMode('__pick__')}
              style={{ height: 'auto', padding: '10px 14px', borderRadius: '8px', textAlign: 'center' }}
            >
              <TeamOutlined style={{ fontSize: '28px', display: 'block', margin: '0 auto 4px' }} />
              Customer picks
            </Button>
          )}
        </div>
        {allowMembers && (() => {
          const sortedMembers = activeMembers
            .slice()
            .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
          // Search appears when faces alone stop working as a picker.
          const needsSearch = sortedMembers.length > 10;
          const q = memberQuery.trim().toLowerCase();
          const visibleMembers = q
            ? sortedMembers.filter((m) => (m.displayName || '').toLowerCase().includes(q))
            : sortedMembers;
          // The current selection never vanishes under a filter.
          const selected = sortedMembers.find((m) => m.clientId === memberMode);
          if (selected && !visibleMembers.includes(selected)) visibleMembers.unshift(selected);
          return (
            <>
              {needsSearch && (
                <Input
                  allowClear
                  placeholder={`Search ${sortedMembers.length} members…`}
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  style={{ marginTop: '12px' }}
                />
              )}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                  marginTop: '12px',
                  ...(needsSearch ? { maxHeight: '300px', overflowY: 'auto', paddingRight: '4px' } : {}),
                }}
              >
                {visibleMembers.map((member) => (
                  <Button
                    key={member.clientId}
                    type={memberMode === member.clientId ? 'primary' : 'default'}
                    onClick={() => setMemberMode(member.clientId)}
                    title={member.displayName || 'Member'}
                    style={{
                      // Fixed footprint: the picker reads as a uniform grid
                      // however long the names run.
                      width: '112px',
                      height: '88px',
                      padding: '10px 8px',
                      borderRadius: '8px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Avatar
                      src={member.avatar && member.avatar.trim() ? member.avatar : undefined}
                      size={36}
                      style={{ display: 'block', margin: '0 auto 4px' }}
                    >
                      {memberInitials(member.displayName)}
                    </Avatar>
                    {firstNameWithInitial(member.displayName) || 'Member'}
                  </Button>
                ))}
                {visibleMembers.length === 0 && (
                  <Text type="secondary">No member matches &ldquo;{memberQuery.trim()}&rdquo;.</Text>
                )}
              </div>
            </>
          );
        })()}
      </Card>
        </>
      )}
      {pooledOnly && billCards}

      </div>
      </Col>
      <Col xs={24} xl={12}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '0 16px 16px', background: '#fafafa' }}>
      {!pooledOnly && billCards}

      {/* Group 2 · Hand off */}
      {devices.length > 0 && (
        <Card style={{ marginTop: '16px', borderRadius: '8px', flex: 1 }} bodyStyle={{ padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
            <Text strong>Complete the payment on…</Text>
            <Link to={`${isAdmin ? '/admin/clients' : '/client/manage'}/${clientId}/teams/${teamId}/tags`} style={{ fontSize: '12px' }}>
              Manage devices
            </Link>
          </div>
          {devices.length > 8 && (
            <Input
              allowClear
              placeholder={`Search ${devices.length} devices…`}
              value={deviceQuery}
              onChange={(e) => setDeviceQuery(e.target.value)}
              style={{ marginBottom: '12px' }}
            />
          )}
          <div style={devices.length > 8 ? { maxHeight: '340px', overflowY: 'auto', paddingRight: '4px' } : undefined}>
          <Row gutter={[12, 12]}>
            {devices.filter((device) => {
              if (!deviceQuery.trim()) return true;
              if (device.id === selectedDeviceId) return true; // selection never vanishes
              const md = device.metadata || {};
              const hay = [md.nickname, md.name, md.placeName, md.place,
                md.unitNumber, md.boxNumber, device.tagUid, device.id,
                device.claimTerm, device.deviceKind].filter(Boolean).join(' ').toLowerCase();
              return hay.includes(deviceQuery.trim().toLowerCase());
            }).map((device) => {
              // Identify the PHYSICAL fixture the operator can see: setup
              // metadata (nickname, place name) first; an unlabeled device
              // falls back to its tagUid / tagId — never the team name,
              // which says nothing about WHICH table this is.
              const md = device.metadata || {};
              // Any operator-meaningful label wins the headline — the SN is a
              // sticker on the back, never the name of a labeled fixture.
              const nickname = md.nickname || md.name || md.placeName || md.place;
              const name = nickname || deviceShortId(device);
              const where = [md.placeName || md.place, md.unitNumber || md.boxNumber || md.id]
                .filter(Boolean).join(' · ');
              const isArmed = !!device.activeTapId
                && device.activeTapExpiresAt
                && device.activeTapExpiresAt.toMillis() > Date.now();
              // The thank-you HOLD (strike-payer #390): the lock dropped at
              // payment success but the glass is still the customer's while
              // the receipt screen is up. Not Ready — a hand-off now is
              // refused as finishing-up.
              const hold = device.activeHandoff && device.activeHandoff.thanksHoldUntil;
              const isFinishing = !isArmed && !!(hold && hold.toMillis && hold.toMillis() > Date.now());
              const armedBy = isArmed && device.activeHandoff
                ? device.activeHandoff.createdByClientId : null;
              const armedByFull = (activeMembers.find((m) => m.clientId === armedBy) || {}).displayName || '';
              const shortName = armedByFull
                ? `${armedByFull.split(' ')[0]}${armedByFull.split(' ')[1] ? ` ${armedByFull.split(' ')[1][0]}.` : ''}`
                : 'a colleague';
              // An Always-on session is started by the CUSTOMER at the glass
              // (createdByClientId 'kiosk'), not by a colleague.
              const armedByLabel = armedBy === clientId ? 'you'
                : (armedBy === 'kiosk' ? 'customer at the terminal' : shortName);
              // ONE state per device, in the staff's words — the same states
              // Ops → Terminals shows (Flow Grid §0/§11, 16 Oct 2026), read
              // from what the tag carries: the tag status, the reader status
              // mirrored on transitions, the session and its screen, the
              // thank-you hold. "Ready" is the card's word for online and
              // free; whether the attract screen is actually up is the
              // registry's knowledge, shown on Ops.
              const deviceState = (() => {
                const terminal = device.deviceKind === 'terminal';
                // (Paused tags never reach this list: the device feed is live tags only.)
                if (terminal && device.readerStatus === 'offline') return { color: 'red', text: 'Offline' };
                if (terminal && device.readerMissingTmr && device.readerMissingTmr === device.tagUid) {
                  return { color: 'orange', text: 'Needs attention — reader not in Stripe' };
                }
                if (isArmed) {
                  const step = device.activeHandoff && device.activeHandoff.flowStep;
                  if (step === 'payment') return { color: 'orange', text: 'Taking payment' };
                  if (armedBy === 'kiosk') return { color: 'orange', text: 'Customer on screen' };
                  const whose = armedBy === clientId ? 'your hand-off' : `${shortName}'s hand-off`;
                  return { color: 'orange', text: `Customer on screen — ${whose}` };
                }
                if (isFinishing) return { color: 'orange', text: 'Finishing up' };
                if (activatingDeviceId === device.id) return { color: 'blue', text: 'Activating…' };
                return { color: 'green', text: 'Ready' };
              })();
              const isSelected = selectedDeviceId === device.id;
              const canCancelThis = isArmed && (armedBy === clientId || canManageSessions);
              const confirmCancel = () => {
                Modal.confirm({
                  title: `End the hand-off on ${name}?`,
                  content: armedBy === clientId
                    ? 'This cancels your live session — the customer can no longer pay it.'
                    : (armedBy === 'kiosk'
                      ? 'A customer started this at the terminal. Cancelling clears their screen and frees the device.'
                      : `Started by ${armedByFull || 'a colleague'}. Cancelling frees the device for the next customer.`),
                  okText: 'Cancel hand-off',
                  okType: 'danger',
                  cancelText: 'Keep it',
                  onOk: () => cancelSession({
                    deviceTagId: device.id,
                    deviceKind: device.deviceKind,
                    refId: device.activeHandoff && device.activeHandoff.refId,
                    createdByClientId: armedBy,
                  }),
                });
              };
              return (
                <Col xs={12} sm={8} key={device.id} style={{ display: 'flex' }}>
                  <Button
                    block
                    disabled={isArmed && !canCancelThis}
                    type={isSelected ? 'primary' : 'default'}
                    onClick={() => (isArmed
                      ? (canCancelThis && confirmCancel())
                      : setSelectedDeviceId(isSelected ? null : device.id))}
                    style={{
                      height: 'auto', minHeight: '184px', padding: '12px', borderRadius: '6px', flex: 1,
                    }}
                  >
                    <Text type="secondary" style={{ display: 'block', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
                      {device.claimTerm || deviceKindLabel(device.deviceKind)}
                    </Text>
                    <div style={{
                      height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px',
                    }}
                    >
                      {/* ONE image source: the TEMPLATE's sign-up image (via the
                          payer redirect), glyph while it loads. Never
                          avatar/topLogo — a device card wearing someone's
                          profile photo is noise, not signal. */}
                      <DeviceImage
                        tag={device}
                        imgStyle={{ maxHeight: '48px', maxWidth: '80px', objectFit: 'contain', borderRadius: '6px' }}
                        iconStyle={{ fontSize: '30px' }}
                      />
                    </div>
                    {/* UNIFORM CARD ANATOMY (consistent displays, easy scan):
                        name slot -> identity lozenge (ALWAYS: SN for terminals,
                        tag head***tail for QR fixtures) -> status -> toggle row.
                        Fixed slot heights keep the wall's rows aligned. */}
                    <div style={{ minHeight: '22px' }}>
                      {nickname
                        ? name
                        : <Text type="secondary" style={{ fontStyle: 'italic' }}>Unnamed</Text>}
                    </div>
                    <div style={{ minHeight: '22px', marginTop: '2px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 8px',
                        borderRadius: '10px',
                        background: '#F4EEE2',
                        border: '1px solid #E3D9C2',
                        color: '#6B5D42',
                        fontSize: '11px',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                      >
                        {deviceShortId(device)}
                      </span>
                    </div>
                    {where && (
                      <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{where}</Text>
                    )}
                    <div>
                      <AntTag
                        color={deviceState.color}
                        style={{ marginTop: '4px', marginRight: 0 }}
                        data-testid="device-state"
                      >
                        {deviceState.text}
                      </AntTag>
                      <div
                        role="none"
                        style={{ marginTop: '6px', minHeight: '24px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {device.deviceKind === 'terminal' && canManageSessions && (
                          <>
                            <Switch
                              size="small"
                              data-testid="idle-toggle"
                              checked={device.terminalIdleMode === 'tip-kiosk'}
                              loading={idleModeBusyId === device.id}
                              onChange={(on) => setIdleMode(device, on)}
                            />
                            <Text type="secondary" style={{ fontSize: '11px', marginLeft: '6px' }}>
                              Always on
                            </Text>
                          </>
                        )}
                      </div>
                    </div>
                  </Button>
                </Col>
              );
            })}
          </Row>
          </div>
          <Button
            type="primary"
            size="large"
            block
            icon={<SendOutlined />}
            loading={arming}
            disabled={!canHandOff}
            onClick={handOff}
            style={{ marginTop: '16px', height: '48px', borderRadius: '6px' }}
          >
            {(() => {
              // The button says exactly what the tap does: the operator
              // SENDS the payment; the customer completes it.
              if (!selectedDevice) return 'Choose a device';
              const md = selectedDevice.metadata || {};
              const label = md.nickname || md.name || md.placeName || md.place || deviceShortId(selectedDevice);
              return `Send to ${label}`;
            })()}
          </Button>
          {selectedDevice && selectedDevice.deviceKind === 'terminal'
            && selectedDevice.terminalIdleMode === 'tip-kiosk' && (
            <Text style={{ display: 'block', marginTop: '8px', color: '#D46B08' }}>
              This terminal is Always on — the hand-off replaces its standing tip
              screen, which returns when the session ends. A customer mid-payment
              shows as Customer on screen and cannot be selected.
            </Text>
          )}
          {canHandOff && selectedDevice && (
            <Text style={{ display: 'block', marginTop: '8px', color: '#27AE60' }}>
              {selectedDevice.deviceKind === 'terminal'
                ? (team.isChargeTotalAmount
                  ? 'Let the customer know the terminal will show the total \u2014 they just tap their phone, or tap or insert a card. They can change the tip on the reader.'
                  : 'Let the customer know the terminal will show the total \u2014 they just tap their phone, or tap or insert a card. The selections can\u2019t be changed on the reader.')
                : 'Let the customer know to scan the QR code once it shows Activated \u2014 they can adjust the selection on their phone.'}
            </Text>
          )}
        </Card>
      )}

      </div>
      </Col>
      </Row>


      {/* Active + recent sessions — FULL WIDTH: room for live progress and the
          receipt, while the form above stays free for the next customer. */}
      {mySessions.map((session) => (
        <Card
          key={session.refId}
          style={{ marginTop: '16px', borderRadius: '8px' }}
          bodyStyle={{
            padding: '12px 14px',
            background: session.status === 'successful' ? '#f6ffed' : undefined,
          }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={8}>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {`${deviceKindLabel(session.deviceKind)} · ${session.deviceName}`}
              </Text>
              <Text strong style={{ display: 'block', fontSize: '15px', marginTop: '2px' }}>
                {session.status === 'created' && (session.deviceKind === 'terminal'
                  ? (session.awaitingConfirm ? 'ON THE READER — awaiting customer confirmation' : 'ON THE READER — waiting for card') : 'WAITING for the customer')}
                {session.status === 'engaged' && 'Customer is paying…'}
                {session.status === 'pending' && 'Payment in progress…'}
                {session.status === 'successful' && 'PAID ✓'}
                {session.status === 'cancelled' && 'Cancelled'}
                {session.status === 'expired' && 'Expired'}
                {session.status === 'failed' && 'FAILED — the reader was not charged'}
                {session.status === 'declined' && 'DECLINED — the customer cancelled on the reader'}
                {!['created', 'engaged', 'pending', 'successful', 'cancelled', 'expired', 'failed', 'declined'].includes(session.status) && `Status: ${session.status}`}
              </Text>
              {['created', 'engaged'].includes(session.status) && session.expiresAt && (
                <SessionCountdown
                  expiresAt={session.expiresAt}
                  onMoreTime={() => moreTime(session)}
                  onExpired={() => setExpiredRefIds((prev) => (prev.includes(session.refId) ? prev : [...prev, session.refId]))}
                />
              )}
              <div style={{ marginTop: '12px' }}>
                {['created', 'engaged', 'pending'].includes(session.status) && (
                  <Button danger onClick={() => cancelSession(session)}>Cancel payment</Button>
                )}
                {['successful', 'cancelled', 'expired', 'failed', 'declined'].includes(session.status) && (
                  <Button type="primary" onClick={() => startNewPayment(session.deviceTagId)}>Done</Button>
                )}
              </div>
              {session.refId && (
                <Text type="secondary" style={{ display: 'block', marginTop: '6px', fontSize: '11px' }}>
                  {`Ref ${session.refId} — also on the customer’s success screen`}
                </Text>
              )}
            </Col>
            <Col xs={24} md={16}>
              {['created', 'engaged', 'pending'].includes(session.status) && (
                <div style={{ maxWidth: '360px' }}>
                  {[
                    ['Service', session.inputTotal != null ? `${symbol}${(session.inputTotal / 100).toFixed(2)}` : null],
                    ['Tip', sessionTipLabel(session)],
                    // The exact amount on the reader — the drive's stamp, so the
                    // panel and the terminal can never disagree.
                    ['Charging', session.deviceKind === 'terminal' && session.chargeTotal != null
                      ? `${symbol}${(session.chargeTotal / 100).toFixed(2)}${session.addOnFees ? ' (incl. fee)' : ''}` : null],
                    ['To', session.teamMemberName
                      || (session.customerPicks ? 'Customer picks the member' : 'Whole team')],
                    ['Customer', session.customerName || null],
                    ['Email', session.customerEmail || null],
                    ['For', session.serviceDescription || null],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <Row justify="space-between" key={k}>
                      <Text type="secondary">{k}</Text>
                      <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</Text>
                    </Row>
                  ))}
                  {session.status === 'created' && (
                    <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                      {session.deviceKind === 'terminal'
                        ? 'The customer taps their card on the terminal. Progress appears here — you can start the next customer above.'
                        : 'The customer scans this device’s QR. Progress appears here — you can start the next customer above.'}
                    </Text>
                  )}
                  {session.status === 'engaged' && (
                    <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                      The customer is on the payment page now.
                    </Text>
                  )}
                </div>
              )}
              {session.status === 'cancelled' && (
                <Text type="secondary">Cancelled — the device is free again.</Text>
              )}
              {session.status === 'expired' && (
                <Text type="secondary">No one picked this up in time — the device is free again.</Text>
              )}
              {session.status === 'successful' && session.tap && (
                <Row gutter={[24, 8]}>
                  <Col xs={24} sm={12}>
                    {[
                      ['Tip', session.tap.amount != null ? `${symbol}${(session.tap.amount / 100).toFixed(2)}` : null],
                      [session.tap.fee > 0 ? 'Fee (accepted by customer)' : 'Fee (absorbed)',
                        session.tap.fee != null ? `${symbol}${(session.tap.fee / 100).toFixed(2)}` : null],
                      ['Total paid', session.tap.amount != null
                        ? `${symbol}${(((session.tap.amount || 0) + (session.tap.fee || 0)) / 100).toFixed(2)}` : null],
                      ['To', session.tap.teamMemberName || session.tap.displayName || null],
                      ['Customer', session.tap.customerName || null],
                      ['Email', session.tap.email || session.tap.sharedEmail || null],
                      ['For', session.tap.serviceDescription || null],
                      ['Customer message', session.tap.paymentInfo || null],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <Row justify="space-between" key={k}>
                        <Text type="secondary">{k}</Text>
                        <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</Text>
                      </Row>
                    ))}
                  </Col>
                  {sites.length > 0 && (
                    <Col xs={24} sm={12}>
                      <Text strong style={{ display: 'block', marginBottom: '4px' }}>Offered to the customer</Text>
                      {sites.map((site) => (
                        <Row justify="space-between" key={site.id}>
                          <Text type="secondary">{site.title || site.displayName || site.type}</Text>
                          <Text type="secondary">{site.type || ''}</Text>
                        </Row>
                      ))}
                    </Col>
                  )}
                </Row>
              )}
            </Col>
          </Row>
        </Card>
      ))}

      <Row gutter={[16, 0]}>
      <Col xs={24}>
      {/* Group 3 — full width, always vertical: share ABOVE recent taps */}
      <Card title="Or share with the customer" style={{ marginTop: '16px', borderRadius: '8px' }}>
        {/* Wraps on narrow phones: the long button drops to its own full-width
            row instead of pinching all three (flex-basis sets the break). */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button disabled={!shareTagId} onClick={() => setIsQRVisible(!isQRVisible)} style={{ flex: '1 1 140px', height: 'auto', padding: '12px', borderRadius: '6px' }}>
            <QrcodeOutlined style={{ fontSize: '20px', display: 'block', margin: '0 auto 4px' }} />
            Show QR
          </Button>
          <Button disabled={!shareTagId} onClick={copyLink} style={{ flex: '1 1 140px', height: 'auto', padding: '12px', borderRadius: '6px' }}>
            <CopyOutlined style={{ fontSize: '20px', display: 'block', margin: '0 auto 4px' }} />
            Copy link
          </Button>
          <Button disabled={!shareTagId} onClick={openTippingPage} style={{ flex: '1 1 140px', height: 'auto', padding: '12px', borderRadius: '6px' }}>
            <ExportOutlined style={{ fontSize: '20px', display: 'block', margin: '0 auto 4px' }} />
            Open tipping page
          </Button>
        </div>

        {!shareTagId && (
          <Text type="secondary" style={{ display: 'block', marginTop: '12px', fontSize: '12px' }}>
            Every tag this team owns is a hand-off device — claim a standard QR
            to share payment links. Device QRs only activate when a payment is sent to them.
          </Text>
        )}

        {shareTagId && isQRVisible && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Image
              src={qrImageSrc}
              alt="Payment QR code"
              width={224}
              preview={false}
              style={{ border: '1px solid #f0f0f0', borderRadius: '8px', padding: '8px', background: '#fff' }}
            />
          </div>
        )}

        {paymentLink && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px',
            background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '6px', padding: '8px 12px',
          }}>
            <LinkOutlined style={{ color: '#999', flexShrink: 0 }} />
            <Text type="secondary" ellipsis style={{ flex: 1, fontSize: '12px' }}>{paymentLink}</Text>
          </div>
        )}
      </Card>

      <Card title="Completed payments" style={{ marginTop: '16px', borderRadius: '8px' }}>
        {posHistory.length === 0 && (
          <Text type="secondary">Completed payments appear here with their full details.</Text>
        )}
        {posHistory
          .filter((row) => canSeeAllPosTaps || row.handoffCreatedByClientId === clientId)
          .map((row) => {
            const paid = ((row.amount || 0) + (row.fee || 0)) / 100;
            const recipient = row.teamMemberName
              ? firstNameWithInitial(row.teamMemberName)
              : (row.isWholeTeamTap ? 'Team' : team.teamName || 'Team');
            const detail = [
              row.fee > 0
                ? `${symbol}${((row.amount || 0) / 100).toFixed(2)} tip + ${symbol}${(row.fee / 100).toFixed(2)} fee`
                : null,
              row.customerName ? `from ${row.customerName}` : null,
              row.serviceDescription || null,
              row.paymentInfo ? `“${row.paymentInfo}”` : null,
            ].filter(Boolean).join(' · ');
            return (
              // TWO lines per payment, every screen size — mobile burned
              // ~150px per row when the grid stacked each field.
              <div key={row.tapId} style={{ padding: '7px 0', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <Text strong style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {row.amount != null ? `${symbol}${paid.toFixed(2)}` : '—'}
                  </Text>
                  <Text style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {`${recipient} · ${row.deviceName}`}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {row.at ? new Date(row.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                  </Text>
                </div>
                {detail && (
                  <Text type="secondary" style={{
                    display: 'block', fontSize: '12px', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {detail}
                  </Text>
                )}
              </div>
            );
          })}
      </Card>

      {devices.length === 0 && (
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: '16px', fontSize: '12px' }}>
          No devices yet — mark a QR as a stand, or claim a terminal, and it appears here.
        </Text>
      )}
      </Col>
      </Row>
    </div>
  );
};

export default TeamSmartPosBody;
